import { BaseService } from '../base/BaseService';

import {
  CONCEPT_COPY_VARIANT_COLUMNS,
  type ConceptCopyRow,
  parseVariantRow,
} from '@/lib/concept-copy/persistence';
import type { ConceptDeliveryTarget } from '@/lib/concept-copy/platform-limits';
import type { ConceptCopyVariant } from '@/lib/concept-copy/types';

/**
 * Copy variants for one Concept — the read side over `ad_units`, plus the thin
 * client for the generation route.
 *
 * 🔴 **Identity.** `ad_units` is ACCOUNT-scoped
 * (`src/lib/identity/table-scope.generated.ts`), so the read filter carries the
 * RESOLVED owner id. Like `ConceptRenditionService` and `AdUnitsService`, this
 * class takes that id as an explicit argument rather than resolving it inside,
 * so a call site cannot forget it by omission — `useConceptCopyVariants` is the
 * one place `resolveOwnerId()` is called.
 *
 * Generation itself is NOT done here. It needs the account's grounding bundle,
 * a frontier model, and a service-side insert, all of which live behind
 * `/api/concepts/copy-variants` — a component calling a model from the browser
 * would ship the prompt and the brand context to the client.
 */

export interface GenerateConceptCopyInput {
  conceptId: string;
  /** Active workspace. The route rejects a concept outside this exact workspace. */
  brandKitId: string;
  /** Caller-owned across an ambiguous transport retry; never generated by the route. */
  commandId: string;
  /** 1–4. The route clamps; passing more is not an error, just capped. */
  count?: number;
  /** "More like this": the variant id the new options must vary from. */
  anchorAdUnitId?: string;
  /** Overrides the Concept topic as the thing being sold. */
  product?: string;
  accessToken: string;
}

export interface GenerateConceptCopyResult {
  variants: ConceptCopyVariant[];
  warnings: { field: string; detail: string; provenance: string }[];
  /** Options that came back as repeats of the anchor and were discarded. */
  discardedEchoes: number;
}

/** The route answers a groundless account with a refusal, not an error. */
export interface ConceptCopyRefusal {
  state: 'refused';
  reason: string;
}

/** Another request owns the current execution lease; poll with the same command. */
export interface ConceptCopyPending {
  state: 'pending';
  retryAfterSeconds: number;
  leaseExpiresAt: string | null;
}

/** `project_tasks.version` + `.current_ad_unit_id` — the optimistic-lock pair
 *  `select_brief_concept_copy_revision` requires as its "expected" arguments. */
export interface ConceptCopyPointer {
  version: number;
  currentAdUnitId: string | null;
}

/**
 * `select_brief_concept_copy_revision`'s success envelope. `history_without_current`
 * is folded into the same shape as `found` — it means the same "here is the current
 * state" read, just one the RPC itself can never actually return (it always sets
 * `current_ad_unit_id` before reading the snapshot back), and callers should treat
 * the two identically.
 */
export interface ConceptCopySelected {
  kind: 'found' | 'history_without_current';
  briefId: string;
  projectTaskId: string;
  conceptVersion: number;
  currentAdUnitId: string | null;
}

/**
 * Every way `select_brief_concept_copy_revision` can refuse a selection, named
 * exactly as the function names them — see
 * `supabase/migrations/20260826220000_ad_copy_platform_registry.sql`. A caller
 * must render a distinct sentence per code; there is no honest generic fallback
 * for "your write did not take."
 */
export type ConceptCopySelectionBlockedCode =
  | 'version_conflict'
  | 'current_pointer_conflict'
  | 'command_context_missing'
  | 'revision_not_selectable'
  | 'platform_not_supported';

export interface ConceptCopySelectionBlocked {
  kind: 'blocked';
  code: ConceptCopySelectionBlockedCode;
  /** Present only for `platform_not_supported`. */
  platform?: string;
}

export type ConceptCopySelectionResult = ConceptCopySelected | ConceptCopySelectionBlocked;

export interface SelectConceptCopyInput {
  briefId: string;
  conceptId: string;
  adUnitId: string;
  /** `project_tasks.version` at the moment this caller last read the concept. */
  expectedConceptVersion: number;
  /** `project_tasks.current_ad_unit_id` at that same read. */
  expectedCurrentAdUnitId: string | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const BLOCKED_CODES: ReadonlySet<string> = new Set([
  'version_conflict',
  'current_pointer_conflict',
  'command_context_missing',
  'revision_not_selectable',
  'platform_not_supported',
]);

/**
 * Parses the RPC's `jsonb` return defensively rather than casting it — this is
 * a hand-shaped envelope, not a generated row type, so a shape the client does
 * not recognise must fail loudly instead of being trusted as a `found` result.
 */
function parseSelectionResult(payload: unknown): ConceptCopySelectionResult {
  if (!isRecord(payload) || typeof payload.kind !== 'string') {
    throw new Error('Copy selection returned an unrecognised response.');
  }
  if (payload.kind === 'blocked') {
    if (typeof payload.code !== 'string' || !BLOCKED_CODES.has(payload.code)) {
      throw new Error('Copy selection was blocked for an unrecognised reason.');
    }
    const blocked: ConceptCopySelectionBlocked = {
      kind: 'blocked',
      code: payload.code as ConceptCopySelectionBlockedCode,
    };
    if (typeof payload.platform === 'string') blocked.platform = payload.platform;
    return blocked;
  }
  if (payload.kind === 'found' || payload.kind === 'history_without_current') {
    const briefId = payload.briefId;
    const projectTaskId = payload.projectTaskId;
    const conceptVersion = payload.conceptVersion;
    const currentAdUnitId = payload.currentAdUnitId;
    if (
      typeof briefId !== 'string' ||
      typeof projectTaskId !== 'string' ||
      typeof conceptVersion !== 'number'
    ) {
      throw new Error('Copy selection returned an incomplete snapshot.');
    }
    if (currentAdUnitId !== null && typeof currentAdUnitId !== 'string') {
      throw new Error('Copy selection returned an incomplete snapshot.');
    }
    // Validated above at runtime; TS does not carry the narrowing of a
    // destructured `unknown` through the two guards, so this asserts what was
    // just checked rather than re-deriving it.
    return {
      kind: payload.kind,
      briefId,
      projectTaskId,
      conceptVersion,
      currentAdUnitId: currentAdUnitId as string | null,
    };
  }
  throw new Error('Copy selection returned an unrecognised response.');
}

export class ConceptCopyVariantService extends BaseService {
  /**
   * Whether a Concept's Brief belongs to the open workspace. A Brief stamped with a workspace
   * belongs to that one. A Brief with no workspace predates stamping and belongs to the account's
   * default workspace (`isBriefInWorkspace`, which the Briefs list applies). Requiring a stamp
   * here read a legacy Brief's Concept as "not in this workspace": its delivery channel came back
   * empty and its current copy came back as none, so the Concept page named no channel and fell
   * back to the oldest option, whatever channel that was written for.
   */
  private async briefInWorkspace(
    projectId: string,
    ownerId: string,
    brandKitId: string,
  ): Promise<boolean> {
    const { data: stamped, error } = await this.client
      .from('projects')
      .select('id')
      .eq('id', projectId)
      .eq('user_id', ownerId)
      .eq('brand_kit_id', brandKitId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (stamped) return true;

    const { data: legacy, error: legacyError } = await this.client
      .from('projects')
      .select('id')
      .eq('id', projectId)
      .eq('user_id', ownerId)
      .is('brand_kit_id', null)
      .maybeSingle();
    if (legacyError) throw new Error(legacyError.message);
    if (!legacy) return false;

    const { data: kit, error: kitError } = await this.client
      .from('brand_kits')
      .select('is_default')
      .eq('id', brandKitId)
      .eq('user_id', ownerId)
      .maybeSingle();
    if (kitError) throw new Error(kitError.message);
    return (kit as { is_default?: boolean } | null)?.is_default === true;
  }

  async list(
    conceptId: string,
    ownerId: string,
    brandKitId: string,
  ): Promise<ConceptCopyVariant[]> {
    const { data, error } = await this.client
      .from('ad_units')
      .select(CONCEPT_COPY_VARIANT_COLUMNS)
      .eq('project_task_id', conceptId)
      .eq('user_id', ownerId)
      .eq('brand_kit_id', brandKitId)
      .order('created_at', { ascending: false });
    if (error) throw new Error(error.message);

    return ((data ?? []) as unknown as ConceptCopyRow[])
      .map((row) => parseVariantRow(row))
      .filter((variant): variant is ConceptCopyVariant => variant !== null);
  }

  /**
   * Where this Concept ships — the input to every character-limit check.
   *
   * `project_task_delivery_intents` is the contracted home of "Delivery
   * platform" (`src/lib/briefs/concept-contract.ts`). It is account-scoped, and
   * a null platform is a real state: 14 of the 32 live rows carry one, which is
   * why the limit layer treats "no platform" as "no ceiling to report" rather
   * than defaulting to a plausible platform's numbers.
   */
  async listDeliveryTargets(
    conceptId: string,
    ownerId: string,
    brandKitId: string,
  ): Promise<ConceptDeliveryTarget[]> {
    // Delivery intents do not carry a workspace column. Verify their Concept's
    // parent Brief first so a known sibling concept id cannot be used as an
    // account-wide read capability.
    const { data: task, error: taskError } = await this.client
      .from('project_tasks')
      .select('project_id')
      .eq('id', conceptId)
      .eq('user_id', ownerId)
      .maybeSingle();
    if (taskError) throw new Error(taskError.message);
    if (!task) return [];

    if (!(await this.briefInWorkspace(task.project_id, ownerId, brandKitId))) return [];

    const { data, error } = await this.client
      .from('project_task_delivery_intents')
      .select('intent, platform')
      .eq('project_task_id', conceptId)
      .eq('user_id', ownerId)
      .order('position', { ascending: true });
    if (error) throw new Error(error.message);

    return ((data ?? []) as unknown as { intent: string; platform: string | null }[])
      .filter((row) => row.intent === 'paid' || row.intent === 'organic')
      .map((row) => ({
        intent: row.intent as ConceptDeliveryTarget['intent'],
        platform: row.platform,
      }));
  }

  /**
   * Generate and persist N variants, optionally anchored on one existing
   * variant.
   *
   * Returns a refusal object rather than throwing when the account has nothing
   * to ground copy in — that is a real, displayable product state ("set up a
   * brand kit first"), not a failure to report as an error toast.
   */
  async generate(
    input: GenerateConceptCopyInput,
  ): Promise<GenerateConceptCopyResult | ConceptCopyRefusal | ConceptCopyPending> {
    if (!input.accessToken) throw new Error('Copy generation requires an access token.');

    const response = await fetch('/api/concepts/copy-variants', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${input.accessToken}`,
      },
      body: JSON.stringify({
        conceptId: input.conceptId,
        brandKitId: input.brandKitId,
        commandId: input.commandId,
        ...(input.count === undefined ? {} : { count: input.count }),
        ...(input.anchorAdUnitId ? { anchorAdUnitId: input.anchorAdUnitId } : {}),
        ...(input.product ? { product: input.product } : {}),
      }),
    });

    const payload = (await response.json().catch(() => null)) as
      | (Partial<GenerateConceptCopyResult> & {
          state?: string;
          reason?: string;
          error?: string;
          retryAfterSeconds?: number;
          leaseExpiresAt?: string | null;
        })
      | null;

    if (!response.ok) {
      throw new Error(payload?.error || `Copy generation failed (${response.status})`);
    }
    if (payload?.state === 'refused') {
      return { state: 'refused', reason: payload.reason ?? 'Nothing on file to ground copy in.' };
    }
    if (payload?.state === 'pending') {
      const retryAfterSeconds =
        typeof payload.retryAfterSeconds === 'number' && Number.isFinite(payload.retryAfterSeconds)
          ? Math.max(1, Math.ceil(payload.retryAfterSeconds))
          : 1;
      return {
        state: 'pending',
        retryAfterSeconds,
        leaseExpiresAt: typeof payload.leaseExpiresAt === 'string' ? payload.leaseExpiresAt : null,
      };
    }
    return {
      variants: payload?.variants ?? [],
      warnings: payload?.warnings ?? [],
      discardedEchoes: payload?.discardedEchoes ?? 0,
    };
  }

  /**
   * The optimistic-concurrency pair `select` must read fresh immediately before
   * every write — `project_tasks` is ACCOUNT-scoped
   * (`src/lib/identity/table-scope.generated.ts`), so this carries the resolved
   * owner id exactly like `list`/`listDeliveryTargets` above. Returns `null`
   * only when the concept itself cannot be read (deleted, or the caller has no
   * access) — a real absence, not a zero-value pointer.
   */
  async getConceptPointer(
    conceptId: string,
    ownerId: string,
    brandKitId: string,
  ): Promise<ConceptCopyPointer | null> {
    const { data, error } = await this.client
      .from('project_tasks')
      .select('project_id, version, current_ad_unit_id')
      .eq('id', conceptId)
      .eq('user_id', ownerId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    const row = data as unknown as {
      project_id: string;
      version: number;
      current_ad_unit_id: string | null;
    } | null;
    if (!row) return null;

    if (!(await this.briefInWorkspace(row.project_id, ownerId, brandKitId))) return null;
    return { version: row.version, currentAdUnitId: row.current_ad_unit_id };
  }

  /**
   * Makes one `ad_units` row the Concept's CURRENT copy — the write the whole
   * paid-lineage chain depends on (`project_tasks.current_ad_unit_id`, read by
   * `assert_paid_media_input_binding_membership` and by
   * `PostService.createPostFromConcept` for organic publish-from-concept alike).
   *
   * Optimistic concurrency, not a blind write: `expectedConceptVersion` and
   * `expectedCurrentAdUnitId` must be a pointer this caller actually read (see
   * `getConceptPointer`) — the RPC compares them against the live row under an
   * advisory lock and returns `{kind:'blocked'}` rather than silently clobbering
   * a concurrent change. Never throws for a blocked selection; that is a real,
   * displayable product state, exactly like `generate`'s refusal/pending states
   * above.
   */
  async select(input: SelectConceptCopyInput): Promise<ConceptCopySelectionResult> {
    const { data, error } = await this.client.rpc('select_brief_concept_copy_revision', {
      p_brief_id: input.briefId,
      p_project_task_id: input.conceptId,
      p_ad_unit_id: input.adUnitId,
      p_expected_concept_version: input.expectedConceptVersion,
      // The generated Args type names this `string` (no `| null`) even though
      // the SQL parameter is a plain nullable `uuid` with no default — the
      // typegen only widens a param to optional when it has a SQL DEFAULT, it
      // never models SQL nullability. `null` is exactly what a Concept with no
      // current copy yet must send here, so the cast is correcting the
      // generated type, not evading a real constraint.
      p_expected_current_ad_unit_id: input.expectedCurrentAdUnitId as string,
    });
    if (error) throw new Error(error.message);
    return parseSelectionResult(data);
  }
}

export const conceptCopyVariantService = new ConceptCopyVariantService();
